import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { apartmentsApi } from '../../api/apartmentsApi';
import type { ApartmentMember } from '../../api/apartmentsApi';

interface ApartmentFeeNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  apartmentId: string;
  apartmentCode: string;
  ownerEmail?: string;
  members: ApartmentMember[];
  onSuccess?: () => void;
}

export const ApartmentFeeNotificationModal: React.FC<ApartmentFeeNotificationModalProps> = ({
  isOpen,
  onClose,
  apartmentId,
  apartmentCode,
  ownerEmail,
  members,
  onSuccess,
}) => {
  const [sendEmail, setSendEmail] = useState(true);
  const [sendPush, setSendPush] = useState(false);
  const [sending, setSending] = useState(false);

  const hasEmail = Boolean(ownerEmail?.trim());
  const hasLinkedApp = useMemo(
    () => members.some((member) => Boolean(member.userId && member.user)),
    [members],
  );

  if (!isOpen) return null;

  const handleSend = async () => {
    const deliveryMethods: ('EMAIL' | 'APP')[] = [];
    if (sendEmail) deliveryMethods.push('EMAIL');
    if (sendPush) deliveryMethods.push('APP');

    if (deliveryMethods.length === 0) {
      toast.error('Vui lòng chọn ít nhất một phương thức gửi thông báo');
      return;
    }

    if (sendEmail && !hasEmail) {
      toast.error('Căn hộ chưa có email nhận thông báo phí');
      return;
    }

    if (sendPush && !hasLinkedApp) {
      toast.error('Cư dân chưa liên kết tài khoản ứng dụng di động');
      return;
    }

    setSending(true);
    try {
      const result = await apartmentsApi.sendApartmentFeeNotification(apartmentId, deliveryMethods);
      if (result.success) {
        const parts: string[] = [];
        if (result.emailsSent > 0) parts.push(`${result.emailsSent} email`);
        if (result.appNotificationsSent > 0) {
          parts.push(`${result.appNotificationsSent} push notification`);
        }
        toast.success(`Đã gửi thông báo phí: ${parts.join(', ')}`);
        onSuccess?.();
        onClose();
      } else {
        toast.error(result.errors.join('; ') || 'Gửi thông báo phí thất bại');
      }
    } catch (error: any) {
      console.error('Error sending fee notification:', error);
      toast.error(error?.response?.data?.message || 'Gửi thông báo phí thất bại');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Gửi thông báo phí - {apartmentCode}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Gửi thông báo phí dịch vụ cho các hóa đơn chưa thanh toán của căn hộ này.
          </p>

          <label className={`flex items-start gap-3 p-3 border rounded-lg ${hasEmail ? 'border-gray-200' : 'border-amber-200 bg-amber-50'}`}>
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900">Gửi Email</div>
              <div className="text-sm text-gray-600">
                {hasEmail ? ownerEmail : 'Chưa có email thông báo phí'}
              </div>
            </div>
          </label>

          <label className={`flex items-start gap-3 p-3 border rounded-lg ${hasLinkedApp ? 'border-gray-200' : 'border-amber-200 bg-amber-50'}`}>
            <input
              type="checkbox"
              checked={sendPush}
              onChange={(e) => setSendPush(e.target.checked)}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900">Gửi Push notification</div>
              <div className="text-sm text-gray-600">
                {hasLinkedApp
                  ? 'Gửi thông báo đến ứng dụng di động của cư dân đã liên kết tài khoản'
                  : 'Chưa có cư dân liên kết tài khoản ứng dụng di động'}
              </div>
            </div>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Hủy
            </button>
            <button
              onClick={handleSend}
              disabled={sending}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded disabled:opacity-50"
            >
              {sending ? 'Đang gửi...' : 'Gửi thông báo phí'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApartmentFeeNotificationModal;
